import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── A buyer-board line's stock position at every location (GEN-2944) ──────
// The Line section's live position and its Stock Elsewhere pop-out, after the
// GAINS Order Builder's Receive SKUL and SKU tabs. Served under
// procurement.replenishmentSuggestions.getLineStockPosition.

export const GetLineStockPositionInputSchema = z.object({
  suggestionId: z.string().uuid(),
});
export type GetLineStockPositionInput = z.infer<
  typeof GetLineStockPositionInputSchema
>;

export const LineStockLocationSchema = z.object({
  warehouseId: z.string().uuid(),
  // The short AS400 location code (e.g. RDDCO); null when not synced.
  warehouseCode: z.string().nullable(),
  warehouseName: z.string(),
  divisionId: z.string().uuid().nullable(),
  divisionName: z.string().nullable(),
  // The short division code buyers use (e.g. W+, QO): the division's AS400
  // code, else the location code's two-character prefix (GEN-3147).
  divisionCode: z.string().nullable(),
  type: z.string().nullable(),
  isDestination: z.boolean(),
  stockStatus: z.string().nullable(),
  popularityCode: z.string().nullable(),
  // Effective (override-overlaid) AS400 min / max.
  policyMin: z.number().nullable(),
  policyMax: z.number().nullable(),
  // The forecast's levels (GAINS Min / Max).
  reorderPoint: z.number().nullable(),
  orderUpToLevel: z.number().nullable(),
  // A buyer's active min floor, and the day it lapses (YYYY-MM-DD; null when
  // it has no end date).
  minFloor: z.number().nullable(),
  minFloorEndsAt: z.string().nullable(),
  onHand: z.number(),
  // Snapshot reserved quantity (AS400 GIUNSHPQ).
  unshipped: z.number(),
  available: z.number(),
  // Dated open PO lines and inbound open TO lines, as the engine reads them.
  onOrder: z.number(),
  transfersIn: z.number(),
  openSalesDemand: z.number(),
  stagedOutbound: z.number(),
  // The engine's net position: on hand + on order + transfers in
  // - max(unshipped, open sales demand) - staged outbound. Unshipped already
  // holds the allocated open sales orders, so they count once.
  netAvailable: z.number(),
  // Units sold this month and the 11 before, without sales-history rules.
  sales12Months: z.number(),
});
export type LineStockLocation = z.infer<typeof LineStockLocationSchema>;

export const GetLineStockPositionResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  destinationWarehouseId: z.string().uuid(),
  // Snapshot date of the destination's stock (YYYY-MM-DD).
  asOf: z.string().nullable(),
  // When the stock feed wrote that snapshot (ISO timestamp), for its time.
  asOfSyncedAt: z.string().nullable(),
  // Destination first, then by net available, largest first.
  locations: z.array(LineStockLocationSchema),
});
export type GetLineStockPositionResponse = z.infer<
  typeof GetLineStockPositionResponseSchema
>;

export const getLineStockPositionContract = oc
  .input(GetLineStockPositionInputSchema)
  .output(GetLineStockPositionResponseSchema)
  .route({
    method: "GET",
    summary: "Get a replenishment line's stock position by location",
    description:
      "For one replenishment suggestion, returns the SKU's stock position at the destination and at every other location that holds or plans it: on hand, unshipped, available, on order, inbound transfers, open sales demand, net available, policy levels, min floor and 12-month sales. The destination comes first, then locations by net available.",
  });
